import React, { useState, useEffect } from 'react';
import './Options.css';

const Options = ({ allTrades = [], addTrade, updateTrade, closeTrade }) => {
  // Filter options trades from all trades
  const options = allTrades.filter(trade => trade.type === 'OPTIONS');
  const [selectedOption, setSelectedOption] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [newOption, setNewOption] = useState({
    pair: 'BTC',
    type: 'call',
    strike: 0,
    expiry: '',
    premium: 0,
    quantity: 1
  });


  const calculateIntrinsicValue = (option) => {
    if (option.type === 'call') {
      return Math.max(0, option.currentPrice - option.strike);
    } else {
      return Math.max(0, option.strike - option.currentPrice);
    }
  };

  const calculateTimeValue = (option) => {
    return option.premium - calculateIntrinsicValue(option);
  };

  const calculateProfitLoss = (option) => {
    const intrinsicValue = calculateIntrinsicValue(option);
    const timeValue = calculateTimeValue(option);
    const totalValue = intrinsicValue + timeValue;
    const cost = option.premium;
    return (totalValue - cost) * option.quantity;
  };

  const addOption = () => {
    const optionTrade = {
      id: Date.now().toString(),
      pair: newOption.pair,
      action: newOption.type === 'call' ? 'BUY' : 'SELL',
      amount: newOption.premium * newOption.quantity,
      leverage: 1, // Options don't use leverage
      entryPrice: newOption.premium,
      currentPrice: newOption.premium,
      timestamp: new Date().toISOString(),
      type: 'OPTIONS',
      status: 'ACTIVE',
      profitLoss: 0,
      profitPercent: 0,
      optionsData: {
        type: newOption.type,
        strike: newOption.strike,
        expiry: newOption.expiry,
        quantity: newOption.quantity,
        delta: Math.random() * 0.8 - 0.4,
        gamma: Math.random() * 0.1,
        theta: -(Math.random() * 20 + 5),
        vega: Math.random() * 50 + 10
      }
    };

    addTrade(optionTrade);
    setNewOption({
      pair: 'BTC',
      type: 'call',
      strike: 0,
      expiry: '',
      premium: 0,
      quantity: 1
    });
  };

  const closeOption = (optionId) => {
    closeTrade(optionId);
  };

  return (
    <div className="options-page">
      {/* Header */}
      <div className="options-header">
        <h2>Options Trading</h2>
        <p>Advanced options strategies and risk management</p>
      </div>

      {/* Options Summary */}
      <div className="options-summary">
        <div className="summary-card">
          <div className="summary-icon">📊</div>
          <div className="summary-content">
            <div className="summary-value">{options.length}</div>
            <div className="summary-label">Total Options</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">💰</div>
          <div className="summary-content">
            <div className="summary-value">
              ${options.reduce((sum, option) => sum + calculateProfitLoss(option), 0).toFixed(2)}
            </div>
            <div className="summary-label">Total P&L</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">⚡</div>
          <div className="summary-content">
            <div className="summary-value">
              {options.filter(option => option.status === 'active').length}
            </div>
            <div className="summary-label">Active Positions</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">🎯</div>
          <div className="summary-content">
            <div className="summary-value">
              {options.length > 0 
                ? (options.reduce((sum, option) => sum + Math.abs(option.delta), 0) / options.length).toFixed(2)
                : 0}
            </div>
            <div className="summary-label">Avg Delta</div>
          </div>
        </div>
      </div>

      {/* Add New Option */}
      <div className="add-option-section">
        <h3>Add New Option</h3>
        <div className="add-option-form">
          <div className="form-row">
            <div className="form-group">
              <label>Pair</label>
              <select
                value={newOption.pair}
                onChange={(e) => setNewOption(prev => ({ ...prev, pair: e.target.value }))}
              >
                <option value="BTC">BTC</option>
                <option value="ETH">ETH</option>
                <option value="SOL">SOL</option>
                <option value="ADA">ADA</option>
                <option value="DOT">DOT</option>
              </select>
            </div>
            <div className="form-group">
              <label>Type</label>
              <select
                value={newOption.type}
                onChange={(e) => setNewOption(prev => ({ ...prev, type: e.target.value }))}
              >
                <option value="call">Call</option>
                <option value="put">Put</option>
              </select>
            </div>
            <div className="form-group">
              <label>Strike Price</label>
              <input
                type="number"
                value={newOption.strike}
                onChange={(e) => setNewOption(prev => ({ ...prev, strike: parseFloat(e.target.value) }))}
                placeholder="Enter strike price"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Expiry Date</label>
              <input
                type="date"
                value={newOption.expiry}
                onChange={(e) => setNewOption(prev => ({ ...prev, expiry: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Premium</label>
              <input
                type="number"
                value={newOption.premium}
                onChange={(e) => setNewOption(prev => ({ ...prev, premium: parseFloat(e.target.value) }))}
                placeholder="Enter premium"
                step="0.01"
              />
            </div>
            <div className="form-group">
              <label>Quantity</label>
              <input
                type="number"
                value={newOption.quantity}
                onChange={(e) => setNewOption(prev => ({ ...prev, quantity: parseInt(e.target.value) }))}
                placeholder="Enter quantity"
                min="1"
              />
            </div>
          </div>
          <button className="add-option-btn" onClick={addOption}>
            Add Option
          </button>
        </div>
      </div>

      {/* Options List */}
      <div className="options-list">
        <h3>Options Positions</h3>
        {options.length === 0 ? (
          <div className="no-options">
            <div className="no-options-icon">📈</div>
            <h4>No Options Positions</h4>
            <p>You haven't created any options trades yet. Use the form above to create your first options position.</p>
          </div>
        ) : (
          <div className="options-grid">
            {options.map(option => {
            const intrinsicValue = calculateIntrinsicValue(option);
            const timeValue = calculateTimeValue(option);
            const profitLoss = calculateProfitLoss(option);
            
            return (
              <div key={option.id} className={`option-card ${option.status}`}>
                <div className="option-header">
                  <div className="option-info">
                    <h4>{option.pair} {option.type.toUpperCase()}</h4>
                    <span className="strike-price">${option.strike.toLocaleString()}</span>
                  </div>
                  <div className="option-status">
                    <span className={`status-badge ${option.status}`}>
                      {option.status === 'active' ? '🟢 Active' : '🔴 Closed'}
                    </span>
                  </div>
                </div>

                <div className="option-details">
                  <div className="price-info">
                    <div className="price-item">
                      <span className="label">Current Price:</span>
                      <span className="value">${option.currentPrice.toLocaleString()}</span>
                    </div>
                    <div className="price-item">
                      <span className="label">Premium:</span>
                      <span className="value">${option.premium.toFixed(2)}</span>
                    </div>
                    <div className="price-item">
                      <span className="label">Expiry:</span>
                      <span className="value">{new Date(option.expiry).toLocaleDateString()}</span>
                    </div>
                  </div>

                  <div className="value-breakdown">
                    <div className="value-item">
                      <span className="label">Intrinsic Value:</span>
                      <span className={`value ${intrinsicValue > 0 ? 'positive' : 'neutral'}`}>
                        ${intrinsicValue.toFixed(2)}
                      </span>
                    </div>
                    <div className="value-item">
                      <span className="label">Time Value:</span>
                      <span className={`value ${timeValue > 0 ? 'positive' : 'negative'}`}>
                        ${timeValue.toFixed(2)}
                      </span>
                    </div>
                    <div className="value-item">
                      <span className="label">Quantity:</span>
                      <span className="value">{option.quantity}</span>
                    </div>
                  </div>

                  <div className="greek-metrics">
                    <div className="greek-item">
                      <span className="label">Delta:</span>
                      <span className="value">{option.delta.toFixed(3)}</span>
                    </div>
                    <div className="greek-item">
                      <span className="label">Gamma:</span>
                      <span className="value">{option.gamma.toFixed(3)}</span>
                    </div>
                    <div className="greek-item">
                      <span className="label">Theta:</span>
                      <span className="value">{option.theta.toFixed(2)}</span>
                    </div>
                    <div className="greek-item">
                      <span className="label">Vega:</span>
                      <span className="value">{option.vega.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="profit-loss">
                    <span className="label">P&L:</span>
                    <span className={`value ${profitLoss >= 0 ? 'positive' : 'negative'}`}>
                      {profitLoss >= 0 ? '+' : ''}${profitLoss.toFixed(2)}
                    </span>
                  </div>
                </div>

                <div className="option-actions">
                  {option.status === 'active' && (
                    <button 
                      className="close-option-btn"
                      onClick={() => closeOption(option.id)}
                    >
                      Close Position
                    </button>
                  )}
                  <button className="details-btn">
                    View Details
                  </button>
                </div>
              </div>
            );
          })}
          </div>
        )}
      </div>

      {/* Options Strategies */}
      <div className="strategies-section">
        <h3>Popular Strategies</h3>
        <div className="strategies-grid">
          <div className="strategy-card">
            <h4>Covered Call</h4>
            <p>Sell call options against owned assets</p>
            <div className="strategy-metrics">
              <span>Risk: Low</span>
              <span>Reward: Limited</span>
            </div>
          </div>
          <div className="strategy-card">
            <h4>Protective Put</h4>
            <p>Buy put options to hedge downside risk</p>
            <div className="strategy-metrics">
              <span>Risk: Low</span>
              <span>Reward: Unlimited</span>
            </div>
          </div>
          <div className="strategy-card">
            <h4>Straddle</h4>
            <p>Buy call and put with same strike</p>
            <div className="strategy-metrics">
              <span>Risk: Limited</span>
              <span>Reward: High</span>
            </div>
          </div>
          <div className="strategy-card">
            <h4>Iron Condor</h4>
            <p>Four-leg strategy for range-bound markets</p>
            <div className="strategy-metrics">
              <span>Risk: Limited</span>
              <span>Reward: Limited</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Options;
